class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.searchTerm = '';
    this.requestNumber = 0;
    if (!this.input.id) this.input.id = `CnPredictiveSearch-${++PredictiveSearch.instanceCount}`;
    this.resultsId = `${this.input.id}-results`;
    this.input.removeAttribute('aria-controls');
    this.input.removeAttribute('aria-owns');
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    // Invalidate immediately, including the debounce window of the native SearchForm.
    this.input.addEventListener('input', () => {
      this.dismissed = false;
      if (this.getQuery() !== this.searchTerm) {
        this.close();
        this.removeAttribute('results');
      }
    });
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  disconnectedCallback() {
    this.dismissed = true;
    this.close();
    clearTimeout(this.focusTimeout);
    clearTimeout(this.statusTimeout);
    this.statusElement?.setAttribute('aria-hidden', 'true');
  }

  getQuery() { return this.input.value.trim(); }

  toggleResetButton() {
    if (this.resetButton) super.toggleResetButton();
  }

  shouldResetForm() { return true; }

  onChange() {
    if (!this.isConnected || this.dismissed) return;
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (newSearchTerm !== this.searchTerm) this.removeAttribute('results');
    this.searchTerm = newSearchTerm;
    if (!newSearchTerm) return this.close(true);
    this.getSearchResults(newSearchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery()) event.preventDefault();
  }

  onFormReset(event) {
    event.preventDefault();
    this.close(true);
    this.searchTerm = '';
    this.toggleResetButton();
    this.input.focus();
  }

  onFocus() {
    this.dismissed = false;
    if (!this.getQuery()) return;
    if (this.searchTerm === this.getQuery() && this.getAttribute('results') === 'true') this.open();
    else this.onChange();
  }

  onFocusOut() {
    clearTimeout(this.focusTimeout);
    this.focusTimeout = setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.dismissed = true;
        this.close();
      }
    }, 0);
  }

  onKeyup() {
    if (!this.getQuery()) this.close(true);
  }

  onKeydown(event) {
    // Composition uses Enter/Escape itself; only the combobox input owns option navigation.
    if (event.isComposing || event.keyCode === 229) return;
    const key = event.key || event.code;
    if (key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.dismissed = true;
      this.close();
      return;
    }
    if (event.target !== this.input || event.altKey || event.ctrlKey || event.metaKey) return;
    if ((key === 'ArrowUp' || key === 'ArrowDown') && this.getAttribute('results') === 'true') {
      event.preventDefault();
      if (!this.isOpen) this.open();
      this.switchOption(key === 'ArrowUp' ? 'up' : 'down');
    } else if (key === 'Enter' && this.isOpen && this.querySelector('[aria-selected="true"]')) {
      event.preventDefault();
      this.selectOption();
    }
  }

  updateSearchForTerm(_previousTerm, newTerm) {
    const label = this.querySelector('[data-predictive-search-search-for-text]');
    if (label) {
      const copy = this.getGeneratedCopy();
      label.textContent = copy.allResults(newTerm);
      label.setAttribute('lang', copy.language);
    }
  }

  getGeneratedCopy() {
    const german = /^de(?:-|$)/i.test(this.ownerDocument.documentElement.lang);
    return german ? {
      language: 'de', suggestions: 'Suchvorschläge',
      unavailable: 'Vorschläge sind gerade nicht verfügbar. Du kannst die vollständige Suche öffnen.',
      allResults: (term) => `Alle Ergebnisse für „${term}“`, openSearch: 'Suche öffnen',
      loadingStatus: 'Suchvorschläge werden geladen.', loadingText: 'Wir suchen passende Ergebnisse …',
      loadingLabel: 'Suchvorschläge werden geladen',
    } : {
      language: 'en', suggestions: 'Search suggestions',
      unavailable: 'Suggestions are currently unavailable. You can open the full search.',
      allResults: (term) => `All results for “${term}”`, openSearch: 'Open search',
      loadingStatus: 'Loading search suggestions.', loadingText: 'Finding matching results …',
      loadingLabel: 'Loading search suggestions',
    };
  }

  switchOption(direction) {
    if (!this.isOpen) return;
    const options = [...this.predictiveSearchResults.querySelectorAll('[role="option"]')].filter(
      (element) => !element.hidden && element.getAttribute('aria-disabled') !== 'true'
    );
    if (!options.length) return;
    const selected = this.querySelector('[aria-selected="true"]');
    const index = options.indexOf(selected);
    const nextIndex = direction === 'up' ? (index <= 0 ? options.length - 1 : index - 1) : (index + 1) % options.length;
    selected?.setAttribute('aria-selected', 'false');
    const next = options[nextIndex];
    next.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', next.id);
    next.scrollIntoView?.({ block: 'nearest' });
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"]');
    (selected?.matches('a,button') ? selected : selected?.querySelector('a,button'))?.click();
  }

  async getSearchResults(searchTerm) {
    this.abortController?.abort();
    this.abortController = new AbortController();
    const requestNumber = ++this.requestNumber;
    const queryKey = searchTerm.toLocaleLowerCase();
    this.setLiveRegionLoadingState();
    if (Object.hasOwn(this.cachedResults, queryKey)) {
      if (this.getQuery() === searchTerm && this.contains(document.activeElement)) this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    const params = new URLSearchParams({ q: searchTerm, 'section_id': 'predictive-search',
      'resources[type]': 'product,collection,page,article,query', 'resources[limit]': '5',
      'resources[limit_scope]': 'each', 'resources[options][unavailable_products]': 'last' });
    try {
      const response = await fetch(`${routes.predictive_search_url}?${params}`, { signal: this.abortController.signal });
      if (!response.ok) throw new Error(`Search ${response.status}`);
      const markup = new DOMParser().parseFromString(await response.text(), 'text/html')
        .querySelector('#shopify-section-predictive-search');
      if (!markup?.querySelector('#predictive-search-results')) throw new Error('Missing search results');
      // Cache only server markup; instance IDs are assigned after insertion.
      document.querySelectorAll('predictive-search').forEach((instance) => {
        if (instance.cachedResults) {
          instance.cachedResults[queryKey] = markup.innerHTML;
          const keys = Object.keys(instance.cachedResults);
          if (keys.length > 30) delete instance.cachedResults[keys[0]];
        }
      });
      if (requestNumber !== this.requestNumber || this.getQuery() !== searchTerm || !this.contains(document.activeElement)) return;
      this.renderSearchResults(markup.innerHTML);
    } catch (error) {
      if (error?.name === 'AbortError' || requestNumber !== this.requestNumber || this.getQuery() !== searchTerm || !this.contains(document.activeElement)) return;
      this.renderSearchError();
    }
  }

  setLiveRegionLoadingState() {
    const copy = this.getGeneratedCopy();
    const loadingText = this.getAttribute('data-loading-text');
    this.setLiveRegionText(loadingText || copy.loadingStatus, loadingText ? null : copy.language);
    this.removeAttribute('results');
    this.input.removeAttribute('aria-activedescendant');
    const loading = document.createElement('div');
    loading.id = this.resultsId;
    loading.className = 'cn-suggestions-loading';
    loading.textContent = copy.loadingText;
    loading.setAttribute('lang', copy.language);
    loading.setAttribute('role', 'listbox');
    loading.setAttribute('aria-label', copy.loadingLabel);
    this.predictiveSearchResults.replaceChildren(loading);
    this.setAttribute('loading', 'true');
    if (this.contains(document.activeElement)) this.open();
  }

  setLiveRegionText(statusText, language = null) {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    if (!this.statusElement) return;
    clearTimeout(this.statusTimeout);
    if (language) this.statusElement.setAttribute('lang', language);
    else this.statusElement.removeAttribute('lang');
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    this.statusTimeout = setTimeout(() => this.statusElement.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const ids = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach((element) => {
      const id = element.id;
      const unique = id === 'predictive-search-results' ? this.resultsId : `${this.input.id}-${id}`;
      ids.set(id, unique);
      element.id = unique;
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby],[aria-describedby],[aria-controls]').forEach((element) => {
      ['aria-labelledby', 'aria-describedby', 'aria-controls'].forEach((attribute) => {
        if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map((id) => ids.get(id) || id).join(' '));
      });
    });
    this.input.removeAttribute('aria-activedescendant');
    this.updateSearchForTerm('', this.getQuery());
    this.setAttribute('results', 'true');
    this.setLiveRegionResults();
    this.open();
  }

  renderSearchError() {
    // A failed suggestion request must leave the ordinary Shopify search usable.
    const copy = this.getGeneratedCopy();
    const box = document.createElement('div');
    box.id = 'predictive-search-results';
    box.className = 'cn-suggestions';
    box.setAttribute('role', 'listbox');
    box.setAttribute('aria-label', copy.suggestions);
    box.setAttribute('lang', copy.language);
    const message = document.createElement('p');
    message.className = 'cn-suggestions-empty';
    message.textContent = copy.unavailable;
    const option = document.createElement('div');
    option.id = 'predictive-search-option-search-keywords';
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', 'false');
    const button = document.createElement('button');
    button.type = 'submit';
    button.tabIndex = -1;
    button.className = 'cn-suggestions-all';
    const label = document.createElement('span');
    label.setAttribute('data-predictive-search-search-for-text', '');
    button.append(label);
    option.append(button);
    box.append(message, option);
    this.renderSearchResults(box.outerHTML);
    this.setLiveRegionText(message.textContent, copy.language);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent;
    if (count) this.setLiveRegionText(count);
    else {
      const copy = this.getGeneratedCopy();
      this.setLiveRegionText(copy.openSearch, copy.language);
    }
  }

  getResultsMaxHeight() {
    const bottom = this.input.form.getBoundingClientRect().bottom;
    return Math.max(160, Math.min(640, window.innerHeight - bottom - 20));
  }

  open() {
    this.input.setAttribute('aria-controls', this.resultsId);
    this.input.setAttribute('aria-owns', this.resultsId);
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) { this.closeResults(clearSearchTerm); }

  closeResults(clearSearchTerm = false) {
    ++this.requestNumber;
    this.abortController?.abort();
    if (clearSearchTerm) {
      this.input.value = '';
      this.searchTerm = '';
      this.removeAttribute('results');
      this.predictiveSearchResults.replaceChildren();
      this.input.removeAttribute('aria-controls');
      this.input.removeAttribute('aria-owns');
    }
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults.style.removeProperty('max-height');
    this.isOpen = false;
  }
}

PredictiveSearch.instanceCount = 0;
customElements.define('predictive-search', PredictiveSearch);
